/**
 * Lectura y validación de la lista de **centinelas de correo** que se teclea en «Correos ignorados».
 *
 * Qué son: el texto que SuccessFactors pone en el campo de correo para decir «esta persona no tiene
 * correo» —`no_mail@sigma-alimentos.com` y compañía—. No son direcciones.
 *
 * 🔴 **Y qué pasa si falta uno**, medido: el 2026-08-07 un centinela tratado como dirección real hizo
 * que el índice único parcial `TenantId_1_Email_1` muriera con `E11000` en el segundo `BulkWrite`, y se
 * llevó **130 000 filas** por delante. Hasta ahora la lista eran ocho valores escritos a mano en el
 * código, que negocio no podía tocar sin un despliegue.
 */

/** Un valor que no se pudo aceptar, con el texto tal cual lo escribió la persona. */
export interface ProblemaCentinela {
  tipo: 'formato' | 'duplicado' | 'normalizado';
  valor: string;
  /** Para `normalizado`: en qué se convirtió. */
  comoQueda?: string;
}

export interface CentinelasLeidos {
  /** Partes locales normalizadas: minúsculas, sin dominio, sin repetidos, en el orden escrito. */
  centinelas: string[];
  problemas: ProblemaCentinela[];
}

/**
 * Una parte local plausible: letras, dígitos y los separadores que admite un correo.
 *
 * No se exige que parezca un centinela (`no*mail*`) a propósito: negocio puede tener marcadores que no
 * lo parezcan, y una regla que los rechazara obligaría a desplegar justo en el caso que este cambio
 * viene a evitar.
 */
const PARTE_LOCAL = /^[a-z0-9]([a-z0-9._%+-]{0,62}[a-z0-9])?$/;

/**
 * 🔴 **Una dirección completa se ACEPTA y se normaliza a su parte local**, avisando de ello.
 *
 * Negocio va a pegar `nomail@sigma-alimentos.com`, porque así viene en el fichero que dio. Guardarlo
 * tal cual dejaría fuera `no_mail@gmail.com`, que **existe** —1 persona medida en QA frente a las 111
 * del dominio de Sigma— y es exactamente el caso que a la primera versión de esta lista se le escapaba.
 * El dominio no aporta nada a la decisión; lo que dice «esto no es un correo» es el nombre.
 *
 * Se avisa en vez de callar porque el efecto es **más amplio** de lo que la persona escribió: pegar
 * `nomail@sigma-alimentos.com` también ignora `nomail@cualquier-otro.com`. Eso hay que verlo.
 */
export function leerCentinelas(texto: string): CentinelasLeidos {
  const centinelas: string[] = [];
  const problemas: ProblemaCentinela[] = [];
  const vistos = new Set<string>();

  for (const bruto of (texto ?? '').split(/[,;\n\r\t]+/)) {
    const token = bruto.trim();
    if (token === '') continue;   // comas de más y líneas vacías son formato humano, no un error

    const enMinusculas = token.toLowerCase();
    const arroba = enMinusculas.indexOf('@');
    const parteLocal = arroba <= 0 ? enMinusculas : enMinusculas.slice(0, arroba);

    if (!PARTE_LOCAL.test(parteLocal)) {
      problemas.push({ tipo: 'formato', valor: token });
      continue;
    }
    if (vistos.has(parteLocal)) {
      problemas.push({ tipo: 'duplicado', valor: token });
      continue;
    }

    // Se avisa DESPUÉS de validar y ANTES de aceptarlo: el valor entra en la lista igualmente.
    if (arroba > 0) problemas.push({ tipo: 'normalizado', valor: token, comoQueda: parteLocal });

    vistos.add(parteLocal);
    centinelas.push(parteLocal);
  }

  return { centinelas, problemas };
}

export interface CambioCentinelas {
  anadidos: string[];
  quitados: string[];
}

/**
 * Qué cambia respecto a lo guardado.
 *
 * 🔴 **Quitar** un centinela es la dirección peligrosa: ese texto pasa a guardarse como si fuera una
 * dirección real, y si dos personas lo comparten vuelve el `E11000` que mató una corrida entera. Por eso
 * el diff se enseña antes de guardar, igual que con los dominios.
 */
export function compararCentinelas(
  antes: readonly string[],
  despues: readonly string[],
): CambioCentinelas {
  const previos = new Set(antes.map((c) => c.toLowerCase()));
  const nuevos = new Set(despues.map((c) => c.toLowerCase()));

  return {
    anadidos: [...nuevos].filter((c) => !previos.has(c)).sort(),
    quitados: [...previos].filter((c) => !nuevos.has(c)).sort(),
  };
}

/** El texto para el `textarea`: uno por línea. */
export function aTextoCentinelas(centinelas: readonly string[]): string {
  return [...centinelas].join('\n');
}
